import type { WorkspaceViewMode } from "./m6";

/**
 * 工作台的信息架构（用户口径，2026-11 修正）。
 *
 * - **顶栏只有三个视图**：故事板 / 画布 / 时间线。它们是**同一份故事数据**的
 *   三种投影，回答「怎么看」，所以放在一起。
 * - **内容导航在左栏**：资料库 / 大纲 / 剧本 / 数据，回答「看哪份内容」。
 *   它们同样是主区视图，进 URL 的 `?view=`，不是浮层、也不是右栏 tab。
 * - 资料库内部的一级分类（世界 / 角色 / 道具 / 术语 / 资产版本）见
 *   `library-tabs.ts`，由左栏的二级导航与资料库视图共用。
 *
 * 浮层仍然保留：镜头卡 chip、世界节点、画布右键等「就地查看某个实体」的入口
 * 依旧打开 `LibraryDialog`，但主导航一律切主区视图。
 */
export type WorkspaceDocView = "outline" | "script";

/** 内容视图：左栏「内容」分组进入。 */
export type WorkspaceContentView = "library" | "outline" | "script" | "data";

/** 全部主区视图：三个投影 + 四个内容视图。 */
export type WorkspaceTab = WorkspaceViewMode | WorkspaceContentView;

export type WorkspaceTabId = WorkspaceTab;

export interface WorkspaceTabDef {
  id: WorkspaceTab;
  label: string;
  /** 快捷键（原型：1/2/3/L/O/S/D）。 */
  key: string;
  /** 悬浮提示 / 快捷键说明。 */
  title?: string;
}

/** 顶栏只放三个投影视图。顺序 = 同一份数据的三种看法。 */
export const WORKSPACE_TABS: WorkspaceTabDef[] = [
  { id: "storyboard", label: "故事板", key: "1", title: "故事板：编辑镜头（快捷键 1）" },
  { id: "canvas", label: "画布", key: "2", title: "画布：场容器 + 镜头卡（快捷键 2）" },
  { id: "timeline", label: "时间线", key: "3", title: "时间线：画面与台词轨（快捷键 3）" },
];

/** 左栏「内容」导航：图标 + 文字，点哪项主区就换哪份内容。 */
export interface ContentNavDef {
  id: WorkspaceContentView;
  label: string;
  key: string;
  title: string;
}

export const CONTENT_NAV: ContentNavDef[] = [
  {
    id: "library",
    label: "资料库",
    key: "L",
    title: "资料库：世界 / 角色 / 道具 / 术语 / 资产版本",
  },
  { id: "outline", label: "大纲", key: "O", title: "大纲：卷 → 单元 → 集" },
  { id: "script", label: "剧本", key: "S", title: "剧本 / 分镜稿" },
  { id: "data", label: "数据", key: "D", title: "数据：领域数据 / 故事板投影 / 画布投影" },
];

const MAIN_VIEWS: WorkspaceTab[] = [
  "storyboard",
  "canvas",
  "timeline",
  "library",
  "outline",
  "script",
  "data",
];

/** 主区视图判定：这七个都能进 `?view=`。 */
export function isMainView(value: string | null | undefined): value is WorkspaceTab {
  return typeof value === "string" && (MAIN_VIEWS as string[]).includes(value);
}

/** `?view=` → 主区视图；缺省 / 非法值一律回故事板（不白屏）。 */
export function parseViewParam(value: string | null | undefined): WorkspaceTab {
  return isMainView(value) ? value : "storyboard";
}

/** 主区视图 → `?view=`；故事板是默认值，不写进 URL。 */
export function viewParamValue(tab: WorkspaceTab): string | null {
  return tab === "storyboard" ? null : tab;
}

/** 快捷键 → tab（大小写都收，和原型一致）。 */
export function tabForKey(key: string): WorkspaceTabId | null {
  if (key === "1") return "storyboard";
  if (key === "2") return "canvas";
  if (key === "3") return "timeline";
  const lower = key.toLowerCase();
  if (lower === "l") return "library";
  if (lower === "o") return "outline";
  if (lower === "s") return "script";
  if (lower === "d") return "data";
  return null;
}

/** 三个投影视图：「同一份数据的三种看法」。 */
export function isProjectionView(tab: WorkspaceTab): tab is WorkspaceViewMode {
  return tab === "storyboard" || tab === "canvas" || tab === "timeline";
}

/** 内容视图（左栏进入）：面包屑要换成内容名，顶栏投影 tab 全部熄灭。 */
export function isContentView(tab: WorkspaceTab): tab is WorkspaceContentView {
  return !isProjectionView(tab);
}

/** 文档类视图（按文档排版）：大纲 / 剧本。 */
export function isDocView(tab: WorkspaceTab): tab is WorkspaceDocView {
  return tab === "outline" || tab === "script";
}

export const CONTENT_VIEW_LABEL: Record<WorkspaceContentView, string> = {
  library: "资料库",
  outline: "大纲",
  script: "剧本 / 分镜稿",
  data: "数据",
};

export const DOC_VIEW_LABEL: Record<WorkspaceDocView, string> = {
  outline: "大纲",
  script: "剧本 / 分镜稿",
};
